import type { Table } from '@tanstack/vue-table';

import { ref, watch, nextTick } from 'vue';

interface UseColumnWidthsOptions<TData> {
  fixedColumnWidths: boolean;
  data: () => TData[];
  table: Table<TData>;
}

export function useColumnWidths<TData>({ fixedColumnWidths, data, table }: UseColumnWidthsOptions<TData>) {
  const columnWidths = ref<Record<string, number>>({});
  const isWidthsCalculated = ref(false);
  const tableRef = ref<HTMLTableElement | null>(null);

  // Расчет ширин колонок из DOM
  const calculateColumnWidths = () => {
    if (!fixedColumnWidths || !tableRef.value || isWidthsCalculated.value) {
      return;
    }

    void nextTick(() => {
      const headerCells = tableRef.value?.querySelectorAll('thead th');
      if (!headerCells || headerCells.length === 0) return;

      const widths: Record<string, number> = {};
      const headerGroups = table.getHeaderGroups();

      headerCells.forEach((cell, index) => {
        const header = headerGroups[0]?.headers[index];
        if (header) {
          const width = cell.getBoundingClientRect().width;
          widths[header.id] = width;
        }
      });

      columnWidths.value = widths;
      isWidthsCalculated.value = true;
    });
  };

  // Получение стилей для колонки с приоритетами
  const getColumnStyle = (headerId: string) => {
    // Получаем header по ID
    const header = table.getHeaderGroups()[0]?.headers.find((h) => h.id === headerId);

    // Приоритет 1: Ручная ширина через meta.style
    if (header?.column.columnDef.meta?.style) {
      return header.column.columnDef.meta.style;
    }

    // Приоритет 2: Гибкая ширина (занимает всё оставшееся место)
    if (header?.column.columnDef.meta?.flexible) {
      return { width: 'auto' };
    }

    // Приоритет 3: Автоматически рассчитанная ширина (если включен fixedColumnWidths)
    if (!fixedColumnWidths || !isWidthsCalculated.value) {
      return undefined;
    }

    const width = columnWidths.value[headerId];
    return width ? { width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` } : undefined;
  };

  // Watch для пересчета при изменении данных
  watch(
    data,
    (newData) => {
      if (fixedColumnWidths && newData.length > 0 && !isWidthsCalculated.value) {
        calculateColumnWidths();
      }
    },
    { immediate: true },
  );

  return {
    columnWidths,
    isWidthsCalculated,
    tableRef,
    getColumnStyle,
    calculateColumnWidths,
  };
}
